import React from 'react';
import { Box, Skeleton, Paper, alpha } from '@mui/material';

const MacroForecastSkeleton = () => {
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={42} height={42} animation="wave" sx={{ borderRadius: 1.5 }} />
          <Box>
            <Skeleton variant="text" width={260} height={24} animation="wave" />
            <Skeleton variant="text" width={200} height={14} animation="wave" sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={200} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
          <Skeleton variant="rounded" width={100} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
          <Skeleton variant="rounded" width={120} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
        </Box>
      </Box>

      {/* Scenario Cards */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2.5,
        mb: 4
      }}>
        {[1, 2, 3, 4].map((i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              p: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              background: 'white',
            }}
          >
            {/* Scenario badge */}
            <Skeleton 
              variant="rounded" 
              width={100} 
              height={24}
              animation="wave"
              sx={{ borderRadius: 3, mb: 2, bgcolor: alpha('#667eea', 0.1) }}
            />
            
            {/* Target name */}
            <Skeleton 
              variant="text" 
              width="80%" 
              height={20}
              animation="wave"
              sx={{ mb: 1 }}
            />
            
            {/* Value */}
            <Skeleton 
              variant="text" 
              width="60%" 
              height={40}
              animation="wave"
              sx={{ mb: 1 }}
            />
            
            {/* Range */}
            <Skeleton 
              variant="text" 
              width="90%" 
              height={16}
              animation="wave"
              sx={{ mb: 2 }}
            />
            
            {/* Progress bar */}
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height={6}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
          </Paper>
        ))}
      </Box>

      {/* Scenario Chart */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'white',
          mb: 4
        }}
      >
        <Skeleton 
          variant="text" 
          width={220} 
          height={32}
          animation="wave"
          sx={{ mb: 3 }}
        />
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={400}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: alpha('#667eea', 0.03) }}
        />
      </Paper>

      {/* Driver Analysis Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'white',
          mb: 4
        }}
      >
        <Skeleton 
          variant="text" 
          width={180} 
          height={32}
          animation="wave"
          sx={{ mb: 3 }}
        />
        
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3
        }}>
          {[1, 2].map((i) => (
            <Box key={i}>
              <Skeleton 
                variant="text" 
                width={150} 
                height={24}
                animation="wave"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[1, 2, 3, 4, 5].map((j) => (
                  <Box
                    key={j}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: alpha('#667eea', 0.02),
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Skeleton 
                      variant="text" 
                      width="40%" 
                      height={20}
                      animation="wave"
                      sx={{ flex: 1 }}
                    />
                    <Skeleton 
                      variant="rectangular" 
                      width="30%" 
                      height={6}
                      animation="wave"
                      sx={{ borderRadius: 3 }}
                    />
                    <Skeleton 
                      variant="text" 
                      width={60} 
                      height={20}
                      animation="wave"
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Backtest Metrics */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'white',
        }}
      >
        <Skeleton 
          variant="text" 
          width={200} 
          height={32}
          animation="wave"
          sx={{ mb: 3 }}
        />
        
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2.5
        }}>
          {[1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: alpha('#667eea', 0.03),
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Skeleton 
                variant="text" 
                width="70%" 
                height={16}
                animation="wave"
                sx={{ mb: 1 }}
              />
              <Skeleton 
                variant="text" 
                width="50%" 
                height={32}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default MacroForecastSkeleton;

